import classNames from 'classnames';
import React, { FC, PropsWithChildren } from 'react';
import ReactMarkdown from 'react-markdown';

import { RemoteDataType } from '../../reducers/remoteData';
import { SavingState } from '../../reducers/savingState';
import { BesvartAttentionMessageSporsmal } from '../../sporsmalsflyt/attentionMessage/attention-message-types';
import { formatChosenCheckboxAlternativesForOppsummerng } from '../../sporsmalsflyt/checkbox/checkbox-types';
import { formatLongDate, formatNumberString, formatShortDate } from '../../sporsmalsflyt/format';
import { isForsikringIngenAlternativerDataType } from '../../sporsmalsflyt/forsikring/forsikring-types';
import { CurrentKontaktInfoInvolvertSporsmal } from '../../sporsmalsflyt/kontaktInfoInvolvert/kontaktInfoInvolvert-types';
import { BesvartPostnummersporsmal } from '../../sporsmalsflyt/postnummer/postnummer-types';
import { BesvartRadiosporsmal } from '../../sporsmalsflyt/radio/radio-types';
import { BesvartSporsmal, CurrentUtleggslinje } from '../../sporsmalsflyt/reducer/current-steg';
import { RadioAlternativ, SelectAlternativ } from '../../sporsmalsflyt/reducer/sporsmalsflyt-types';
import { BesvartSelectSporsmal } from '../../sporsmalsflyt/select/select-types';
import { isForsikringIngenAlternativerType } from '../../sporsmalsflyt/skadedato-and-forsikring/skadedato-and-forsikring-types';
import { CurrentSkadelidtInfoSporsmal } from '../../sporsmalsflyt/skadelidtInfo/skadelidtInfo-types';
import { VerkstedVisning } from '../../sporsmalsflyt/verksted/verksted';
import { BesvartVerkstedsporsmal } from '../../sporsmalsflyt/verksted/verksted-types';
import { AttentionMessageType, SporsmalType, UtleggType } from '../../types/generated-types';
import { assertUnreachable } from '../../utils/compiler-utils';

import './oppsummering.scss';

export const saveButtonProps = (
    savingStateSporsmalsflyt: SavingState,
    onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void,
    savingStateForsikringsinfo: SavingState = SavingState.Success,
) => {
    if (savingStateForsikringsinfo === SavingState.Saving) {
        return { text: 'Alt stemmer, send inn!', disabled: true, onClick };
    }

    switch (savingStateSporsmalsflyt) {
        case SavingState.Success:
        case SavingState.Initial:
            return { text: 'Alt stemmer, send inn!', onClick };
        case SavingState.Saving:
            return { text: 'Sender inn...', disabled: true, onClick };
        case SavingState.Failure:
        case SavingState.UgyldigVedlegg:
            return { text: 'Prøv igjen', onClick };
        case SavingState.UnauthorizedUser:
        case SavingState.BadRequestFailure:
            return { text: 'Alt stemmer, send inn!', disabled: true, onClick };
        default:
            return assertUnreachable(savingStateSporsmalsflyt);
    }
};

export const BesvartSporsmalRad = ({ sporsmal, svar }: { sporsmal: string; svar: string }) => (
    <tr className="oppsummering-besvart-sporsmal-rad">
        <td>{sporsmal}</td>
        <td className="oppsummering-table-svar">
            {svar.split('\n').map((s) => (
                <React.Fragment key={s}>
                    {s} <br />
                </React.Fragment>
            ))}
        </td>
    </tr>
);

export const BesvartKontaktInfoInvolvertSporsmalRad = ({
    besvartSporsmal,
}: {
    besvartSporsmal: CurrentKontaktInfoInvolvertSporsmal;
}) => (
    <>
        <tr className="oppsummering-besvart-sporsmal-rad">
            <td>Navn</td>
            <td className="oppsummering-table-svar">{besvartSporsmal.navn}</td>
        </tr>
        {besvartSporsmal.mobilnummer.length > 0 && (
            <tr className="oppsummering-besvart-sporsmal-rad">
                <td>Mobilnummer (valgfritt)</td>
                <td className="oppsummering-table-svar">{besvartSporsmal.mobilnummer}</td>
            </tr>
        )}
        {besvartSporsmal.adresse.length > 0 && (
            <tr className="oppsummering-besvart-sporsmal-rad">
                <td>Adresse (valgfritt)</td>
                <td className="oppsummering-table-svar">{besvartSporsmal.adresse}</td>
            </tr>
        )}
        {besvartSporsmal.fodselsdato.length > 0 && (
            <tr className="oppsummering-besvart-sporsmal-rad">
                <td>Fødselsdato (valgfritt)</td>
                <td className="oppsummering-table-svar">{besvartSporsmal.fodselsdato}</td>
            </tr>
        )}
    </>
);

export const BesvartSkadelidtInfoSporsmalRad = ({
    besvartSporsmal,
}: {
    besvartSporsmal: CurrentSkadelidtInfoSporsmal;
}) => {
    const { fulltNavn, fodselsnummer, fastlegeKontor, hasBankIdOgMobil, mobilnummer } =
        besvartSporsmal.sporsmal.skadelidtInfoSporsmalFieldLabels;
    return (
        <>
            <tr className="oppsummering-besvart-sporsmal-rad">
                <td>{fulltNavn}</td>
                <td className="oppsummering-table-svar">{besvartSporsmal.fulltNavn}</td>
            </tr>
            <tr className="oppsummering-besvart-sporsmal-rad">
                <td>{fodselsnummer}</td>
                <td className="oppsummering-table-svar">{besvartSporsmal.fodselsnummer}</td>
            </tr>
            <tr className="oppsummering-besvart-sporsmal-rad">
                <td>{fastlegeKontor}</td>
                <td className="oppsummering-table-svar">{besvartSporsmal.fastlegeKontor}</td>
            </tr>
            <tr className="oppsummering-besvart-sporsmal-rad">
                <td>{hasBankIdOgMobil}</td>
                <td className="oppsummering-table-svar">
                    {besvartSporsmal.hasBankIdOgMobil ? 'Ja' : `Nei. ${besvartSporsmal.sporsmal.infoTextSecondary}`}
                </td>
            </tr>
            {besvartSporsmal.hasBankIdOgMobil && (
                <tr className="oppsummering-besvart-sporsmal-rad">
                    <td>{mobilnummer}</td>
                    <td className="oppsummering-table-svar">{besvartSporsmal.mobilnummer}</td>
                </tr>
            )}
        </>
    );
};

export const BesvartRadioSporsmalRad = ({
    sporsmal,
    svar,
    oppsummeringsInfo,
}: {
    sporsmal: string;
    svar: string;
    oppsummeringsInfo?: string;
}) => (
    <tr className="oppsummering-besvart-sporsmal-rad">
        <td className={classNames({ 'oppsummering-table--align-top ': oppsummeringsInfo })}>{sporsmal}</td>
        <td className="oppsummering-table-svar">
            {svar.split('\n').map((s) => (
                <React.Fragment key={s}>
                    {s}
                    {oppsummeringsInfo && (
                        <>
                            <br />
                            <br />
                            <span className="radio-oppsummerings-info">
                                <ReactMarkdown>{oppsummeringsInfo}</ReactMarkdown>
                            </span>
                        </>
                    )}
                </React.Fragment>
            ))}
        </td>
    </tr>
);

const BesvartSporsmalElementRad = ({
    sporsmal,
    svar,
    alignTop = false,
}: {
    sporsmal: string;
    svar: JSX.Element;
    alignTop: boolean;
}) => (
    <tr className="oppsummering-besvart-sporsmal-rad">
        <td className={classNames({ 'oppsummering-table--align-top': alignTop })}>{sporsmal}</td>
        <td className="oppsummering-table-svar">{svar}</td>
    </tr>
);

const BesvartPostnummerSporsmalRad = ({ besvartSporsmal }: { besvartSporsmal: BesvartPostnummersporsmal }) => (
    <>
        <tr className="oppsummering-besvart-sporsmal-rad">
            <td>Postnummer</td>
            <td className="oppsummering-table-svar">{besvartSporsmal.postnummer}</td>
        </tr>
        <tr className="oppsummering-besvart-sporsmal-rad">
            <td>Poststed</td>
            <td className="oppsummering-table-svar">
                {besvartSporsmal.poststed.type === RemoteDataType.SUCCESS ? besvartSporsmal.poststed.data : ''}
            </td>
        </tr>
    </>
);

const datoInformation = (linje: CurrentUtleggslinje) =>
    (linje.dato ? `(${formatShortDate(linje.dato)})` : '').toString();

const calculateOnsketErstatning = (linje: CurrentUtleggslinje) => {
    if (linje.utleggType === UtleggType.WithRefusjon) {
        return Number(linje.refundert) > Number(linje.utgift) ? 0 : Number(linje.utgift) - Number(linje.refundert);
    }
    return Number(linje.dagensPris);
};

const viewAnbefaltVerksteder = (besvartSporsmal: BesvartVerkstedsporsmal): JSX.Element => {
    if (besvartSporsmal.verksteder.type !== RemoteDataType.SUCCESS) {
        return null;
    }

    if (besvartSporsmal.verksteder.data.length === 0) {
        return (
            <>
                <p className="recommended-garage-info">Vi fant ingen verksteder i nærheten av deg som vi anbefaler.</p>
                <p>
                    Vi ber deg ta kontakt med et autorisert verksted for taksering og reparasjon, verkstedet må benytte
                    takseringssystemet DBS. Oppgi ditt skadenummer til verkstedet ved taksering.
                </p>
            </>
        );
    }

    return (
        <>
            <p className="recommended-garage-info">
                Ta kontakt med ett av disse verkstedene for å avtale tid for vurdering av skadene på kjøretøyet. Dette
                gjør du etter at du har sendt inn skademeldingen.
            </p>
            <ol className="address-list">
                {besvartSporsmal.verksteder.data.map((v) => (
                    <VerkstedVisning key={`${v.postnummer}-${v.navn}`} verksted={v} />
                ))}
            </ol>
        </>
    );
};

export const UtleggLinje = ({ linje }: { linje: CurrentUtleggslinje }) => (
    <BesvartSporsmalRad
        sporsmal={linje.beskrivelse}
        svar={`${calculateOnsketErstatning(linje)} kr ${datoInformation(linje)}`}
    />
);

interface OppsummeringTabellProps {
    header: string;
}

export const OppsummeringTabell: FC<PropsWithChildren<OppsummeringTabellProps>> = ({ header, children }) => (
    <div className="oppsummering-svar-bolk">
        <h3 className="oppsummering-subtitle">{header}</h3>
        <table className="oppsummering-table">
            <tbody>{children}</tbody>
        </table>
    </div>
);

const getSelectedRadioSvarAlternative = (besvartSporsmal: BesvartRadiosporsmal): RadioAlternativ => {
    return besvartSporsmal.sporsmal.alternativer.find((alternativ) => alternativ.id === besvartSporsmal.svarId);
};

const getSelectedSelectSvarAlternative = (besvartSporsmal: BesvartSelectSporsmal): SelectAlternativ =>
    besvartSporsmal.sporsmal.alternativer.find((alternativ) => alternativ.id === besvartSporsmal.svarId);

const getAttentionMessageSubHeadingBesvartSporsmalRad = (
    besvartSporsmal: BesvartAttentionMessageSporsmal,
): React.JSX.Element => {
    return besvartSporsmal.sporsmal.attentionMessageType === AttentionMessageType.SubHeading ? (
        <tr className="oppsummering-besvart-sporsmal-rad oppsummering-besvart-sporsmal-rad--sub-heading">
            <td colSpan={2}>{besvartSporsmal.sporsmal.tittel}</td>
        </tr>
    ) : null;
};

export const ViewBesvartSporsmal = ({ besvartSporsmal }: { besvartSporsmal: BesvartSporsmal }) => {
    switch (besvartSporsmal.type) {
        case SporsmalType.Radio:
            return (
                <BesvartRadioSporsmalRad
                    key={besvartSporsmal.sporsmal.id}
                    sporsmal={besvartSporsmal.sporsmal.tekst}
                    svar={getSelectedRadioSvarAlternative(besvartSporsmal).tekst}
                    oppsummeringsInfo={getSelectedRadioSvarAlternative(besvartSporsmal).oppsummeringsInfo}
                />
            );
        case SporsmalType.Select:
            return (
                <BesvartSporsmalRad
                    key={besvartSporsmal.sporsmal.id}
                    sporsmal={besvartSporsmal.sporsmal.tekst}
                    svar={getSelectedSelectSvarAlternative(besvartSporsmal).tekst}
                />
            );
        case SporsmalType.Checkbox:
            return (
                <BesvartSporsmalRad
                    key={besvartSporsmal.sporsmal.id}
                    sporsmal={besvartSporsmal.sporsmal.tekst}
                    svar={formatChosenCheckboxAlternativesForOppsummerng(besvartSporsmal.alternativer)}
                />
            );
        case SporsmalType.Forsikring:
            if (isForsikringIngenAlternativerDataType(besvartSporsmal.data)) {
                return (
                    <BesvartSporsmalRad
                        key={besvartSporsmal.sporsmal.id}
                        sporsmal={besvartSporsmal.sporsmal.tekst}
                        svar={besvartSporsmal.data.forsikringsnummer}
                    />
                );
            }
            return (
                <BesvartSporsmalRad
                    key={besvartSporsmal.sporsmal.id}
                    sporsmal={besvartSporsmal.sporsmal.tekst}
                    svar={besvartSporsmal.data.selectedAlternativ.tekst}
                />
            );

        case SporsmalType.SkadedatoAndForsikring:
            if (isForsikringIngenAlternativerType(besvartSporsmal.forsikringData)) {
                return (
                    <>
                        <BesvartSporsmalRad
                            key={`${besvartSporsmal.sporsmal.id}-skadedato`}
                            sporsmal={besvartSporsmal.sporsmal.skadedatoTittel}
                            svar={formatLongDate(besvartSporsmal.skadedato)}
                        />
                    </>
                );
            }
            return (
                <>
                    <BesvartSporsmalRad
                        key={`${besvartSporsmal.sporsmal.id}-skadedato`}
                        sporsmal={besvartSporsmal.sporsmal.skadedatoTittel}
                        svar={formatLongDate(besvartSporsmal.skadedato)}
                    />
                    <BesvartSporsmalRad
                        key={`${besvartSporsmal.sporsmal.id}-forsikring`}
                        sporsmal={besvartSporsmal.sporsmal.forsikringTittel}
                        svar={besvartSporsmal.forsikringData.selectedAlternativ.tekst}
                    />
                </>
            );
        case SporsmalType.Tekst:
            return (
                <BesvartSporsmalRad
                    key={besvartSporsmal.sporsmal.id}
                    sporsmal={besvartSporsmal.sporsmal.tekst}
                    svar={besvartSporsmal.svarTekst}
                />
            );
        case SporsmalType.Kredittkortnummer:
            return (
                <BesvartSporsmalRad
                    key={besvartSporsmal.sporsmal.id}
                    sporsmal={besvartSporsmal.sporsmal.tekst}
                    svar={besvartSporsmal.kredittkortnummer}
                />
            );
        case SporsmalType.Dato:
            return (
                <BesvartSporsmalRad
                    key={besvartSporsmal.sporsmal.id}
                    sporsmal={besvartSporsmal.sporsmal.tittel}
                    svar={formatLongDate(besvartSporsmal.dato)}
                />
            );
        case SporsmalType.Tall:
            return (
                <BesvartSporsmalRad
                    key={besvartSporsmal.sporsmal.id}
                    sporsmal={besvartSporsmal.sporsmal.tittel}
                    svar={
                        formatNumberString(besvartSporsmal.tall.toString()) +
                        (besvartSporsmal.sporsmal.maleenhet ? ` ${besvartSporsmal.sporsmal.maleenhet}` : '')
                    }
                />
            );

        case SporsmalType.Skadenummer:
            return (
                <BesvartSporsmalRad
                    key={`${besvartSporsmal.sporsmal.id}-skadenummer`}
                    sporsmal={besvartSporsmal.sporsmal.tekst || 'Skadenummer'}
                    svar={besvartSporsmal.skadenummer}
                />
            );

        case SporsmalType.Forsikringsnummer:
            return (
                <BesvartSporsmalRad
                    key={`${besvartSporsmal.sporsmal.id}-forsikringsnummer`}
                    sporsmal={besvartSporsmal.sporsmal.tekst}
                    svar={besvartSporsmal.forsikringsnummer}
                />
            );

        case SporsmalType.SimpleKontaktinfo:
            return (
                <BesvartSporsmalRad
                    key={`${besvartSporsmal.sporsmal.id}-simplekontaktinfo`}
                    sporsmal={besvartSporsmal.sporsmal.tittel || 'Kontaktinformasjon--'}
                    svar={`${besvartSporsmal.fulltNavn}\n${besvartSporsmal.mobilnummer}\n${besvartSporsmal.fodselsnummer}`}
                />
            );

        case SporsmalType.KontaktInfoInvolvert:
            return <BesvartKontaktInfoInvolvertSporsmalRad besvartSporsmal={besvartSporsmal} />;

        case SporsmalType.SkadelidtInfo:
            return <BesvartSkadelidtInfoSporsmalRad besvartSporsmal={besvartSporsmal} />;

        case SporsmalType.Verksted:
            return (
                <BesvartSporsmalElementRad
                    sporsmal="Dine verkstedsalternativer"
                    svar={viewAnbefaltVerksteder(besvartSporsmal)}
                    alignTop
                />
            );

        case SporsmalType.Postnummer:
            return <BesvartPostnummerSporsmalRad besvartSporsmal={besvartSporsmal} />;

        case SporsmalType.SporsmalSide:
            return (
                <>
                    {besvartSporsmal.sporsmal.showTitle && (
                        <tr className="oppsummering-table-title">
                            <th colSpan={2}>
                                <h3 className="oppsummering-list-subtitle">{besvartSporsmal.sporsmal.tittel}</h3>
                            </th>
                        </tr>
                    )}
                    {besvartSporsmal.besvarteSporsmal.map((b) => (
                        <ViewBesvartSporsmal besvartSporsmal={b} key={b.sporsmal.id} />
                    ))}
                </>
            );
        case SporsmalType.SporsmalList:
            return (
                <>
                    <tr className="oppsummering-besvart-sporsmal-rad">
                        <td className="oppsummering-sporsmal-list--list-table-cell" colSpan={2}>
                            {besvartSporsmal.besvarteSporsmal.map((besvartSporsmalList, k) => {
                                return (
                                    <table
                                        className="oppsummering-sporsmal-list--list-table"
                                        key={`oppsummering-sporsmal-list--list-table-${k + 1}`}
                                    >
                                        <tbody>
                                            {besvartSporsmalList.map((besvartSporsmal) => (
                                                <ViewBesvartSporsmal
                                                    besvartSporsmal={besvartSporsmal}
                                                    key={besvartSporsmal.sporsmal.id}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                );
                            })}
                        </td>
                    </tr>
                </>
            );
        case SporsmalType.ForsikringObjekterCheckbox:
            return (
                <BesvartSporsmalRad
                    sporsmal={besvartSporsmal.sporsmal.tekst}
                    svar={besvartSporsmal.objekter
                        .filter((objekt) => objekt.checked)
                        .map((objekt) => objekt.objektnavn)
                        .join(', ')}
                />
            );

        case SporsmalType.AttentionMessage:
            return getAttentionMessageSubHeadingBesvartSporsmalRad(besvartSporsmal);

        case SporsmalType.Informasjon:
        case SporsmalType.Kontaktinfo:
        case SporsmalType.Filopplasting:
        case SporsmalType.Utlegg:
        case SporsmalType.Card:
        case SporsmalType.Modal:
            return null;
        default:
            return assertUnreachable(besvartSporsmal);
    }
};
